{
  "title": "Approval workflows",
  "actions": {
    "new": "New workflow",
    "edit": "Edit",
    "test": "Test",
    "duplicate": "Duplicate",
    "disable": "Disable",
    "enable": "Enable",
    "viewRuns": "View requests",
    "delete": "Delete",
    "cancel": "Cancel",
    "saveDraft": "Save draft",
    "publish": "Publish",
    "discardDraft": "Discard draft",
    "more": "More actions",
    "rowActions": "Actions for {{name}}"
  },
  "fields": {
    "name": "Workflow",
    "nameEn": "Name (English)",
    "nameAr": "Name (Arabic)",
    "description": "Description",
    "subject": "Document type",
    "trigger": "Trigger",
    "scope": "Applies to",
    "priority": "Order",
    "published": "Published",
    "state": "State",
    "requests": "Requests now",
    "updated": "Last updated",
    "created": "Created"
  },
  "trigger": {
    "purchase": "Purchase submitted for approval",
    "project_budget": "Project budget submitted for approval",
    "when": {
      "purchase": "When a purchase is submitted for approval",
      "project_budget": "When a project budget is submitted for approval",
      "project_variation": "When a project variation is sent for internal approval",
      "project_claim": "When a progress claim is sent for internal approval",
      "service_contract": "When a maintenance contract is activated and needs approval"
    },
    "project_variation": "Project variation sent for internal approval",
    "project_claim": "Progress claim sent for internal approval",
    "service_contract": "Maintenance contract sent for approval"
  },
  "state": {
    "active": "Active",
    "disabled": "Disabled",
    "draftOnly": "Draft only",
    "draftChanges": "Unpublished changes",
    "explain": {
      "active": "Active: new submissions that match are routed through the published version.",
      "disabled": "Disabled: new submissions skip this workflow. Requests already in progress finish on their version.",
      "draftOnly": "Draft only: never published, so it routes nothing yet."
    }
  },
  "counts": {
    "inProgress_one": "{{n}} in progress",
    "inProgress_other": "{{n}} in progress",
    "blocked_one": "{{n}} blocked",
    "blocked_other": "{{n}} blocked",
    "failed_one": "{{n}} failed",
    "failed_other": "{{n}} failed"
  },
  "list": {
    "title": "Approval workflows",
    "subtitle": "Which documents need approval, who approves them and in what order.",
    "tableLabel": "Approval workflows",
    "searchPlaceholder": "Search workflow names…",
    "searchLabel": "Search workflows",
    "clearSearch": "Clear search",
    "stateFilter": "State",
    "allStates": "All states",
    "stateOption": {
      "active": "Active",
      "disabled": "Disabled",
      "unpublished": "Draft only"
    },
    "subjectFilter": "Document type",
    "allSubjects": "All document types",
    "clearFilters": "Clear filters",
    "runsLink": "Approval requests",
    "noSpec": "Not configured yet",
    "viewRunsOf": "Requests of {{name}}",
    "empty": {
      "title": "No approval workflows yet",
      "body": "A workflow decides which documents (purchases, project budgets) must be approved, who approves them, and in what order. Without one, whoever holds the permission posts or approves them directly.",
      "bodyEdit": "Create one, test it with sample values, then publish it.",
      "filteredTitle": "No workflows match",
      "filteredBody": "Try another name, state or document type."
    }
  },
  "create": {
    "title": "New workflow",
    "body": "You configure the conditions and the approval steps next. Nothing changes until you publish.",
    "startBody": "Start from a blank workflow, or from a template you adapt to your company. Nothing changes until you publish.",
    "templateBody": "Set the amounts and who approves. The workflow is created as a draft: review and test it in the builder, then publish it.",
    "startFrom": "Start from",
    "blank": "Blank workflow",
    "blankHint": "Set the conditions and approval steps yourself in the builder.",
    "template": "Template",
    "change": "Change",
    "amounts": "Amounts",
    "paramHint": "Suggested: {{amount}}",
    "descriptionHint": "What this workflow is for, for the people who maintain it.",
    "subjectHint": "The document type whose submissions it routes.",
    "submit": "Create workflow",
    "continue": "Continue",
    "back": "Back"
  },
  "templates": {
    "steps": "Steps",
    "then": " → ",
    "everyone": "{{step}} (everyone)",
    "above": "{{step}} (above {{amount}})",
    "applies": "Above {{amount}}: {{steps}}"
  },
  "disable": {
    "title": "Disable {{name}}?",
    "body": "New submissions will no longer be routed through this workflow."
  },
  "enable": {
    "title": "Enable {{name}}?",
    "body": "New matching submissions will be routed through its published version again."
  },
  "delete": {
    "title": "Delete {{name}}?",
    "body": "The workflow and its draft are deleted. This cannot be undone.",
    "publishedReason": "Published workflows are kept for their history. Disable it instead."
  },
  "discard": {
    "title": "Discard the draft?",
    "body": "Your unpublished changes are removed. Version {{n}} stays published."
  },
  "impact": {
    "running_one": "{{n}} request in progress keeps its current steps.",
    "running_other": "{{n}} requests in progress keep their current steps."
  },
  "leave": {
    "title": "Leave without saving?",
    "body": "Your changes to this workflow are not saved yet.",
    "confirm": "Leave",
    "cancel": "Stay"
  },
  "toast": {
    "created": "Workflow created",
    "createdFromTemplate": "Workflow created as a draft. Review and test it, then publish.",
    "duplicated": "Copy created: {{name}}",
    "disabled": "{{name}} disabled",
    "enabled": "{{name}} enabled",
    "deleted": "{{name}} deleted",
    "draftSaved": "Draft saved",
    "discarded": "Draft discarded",
    "published": "Version {{n}} published",
    "priority": "Order changed to {{n}}"
  },
  "errors": {
    "WORKFLOW_ACTION_NOT_ALLOWED": "You cannot publish a workflow that posts purchases automatically: you do not have permission to post purchases.",
    "WORKFLOW_PRIORITY_TAKEN": "Another workflow for this document type already has that order number.",
    "WORKFLOW_INVALID": "The workflow cannot be saved or published as it is. Fix the marked problems.",
    "VERSION_CONFLICT": "Someone else changed this workflow. Reload it and try again."
  },
  "problems": {
    "unknownField": "Choose a field.",
    "badOperator": "This comparison does not apply to the field.",
    "noValue": "This comparison needs no value.",
    "chooseValues": "Choose at least one value.",
    "amount": "Enter an amount with at most 3 decimals.",
    "chooseValue": "Choose a value.",
    "duplicateKey": "Two steps have the same key.",
    "listedTwice": "Listed twice.",
    "noAssignees": "Choose who approves this step.",
    "reminderAfterDue": "The reminder must come before the due time.",
    "noSteps": "Add at least one approval step.",
    "allConditional": "At least one step must always run, so a matching purchase can never pass with no approver.",
    "badAction": "This action is not available for the document type.",
    "noDraft": "There is no draft to publish.",
    "missingAssignee": "This role or user no longer exists.",
    "taken": "Already used by another workflow.",
    "nameStep": "Name the step.",
    "nameWorkflow": "Name the workflow.",
    "tooDeep": "Conditions can be nested at most 3 levels deep.",
    "enterAmount": "Enter an amount.",
    "amountPositive": "The amount must be more than zero.",
    "amountAbove": "This amount must be more than the first amount.",
    "unknownSlot": "This template has no such group of approvers.",
    "unknownAmount": "This template has no such amount.",
    "number": "Enter a number.",
    "date": "Enter a date.",
    "text": "Enter a value.",
    "enumValue": "Choose one of the listed values.",
    "timerUnits": "The reminder and the due time must use the same unit.",
    "noFallback": "Choose the role that decides when no head can be found.",
    "orgField": "Choose a field that holds an org unit.",
    "requesterField": "The requester’s home unit is used: no field is needed.",
    "timerShapes": "Give the time either in hours or with a unit.",
    "missingFallbackRole": "This role no longer exists.",
    "atMost": "At most {{max}}."
  },
  "checks": {
    "MISSING_ASSIGNEE": "This role or user no longer exists.",
    "MISSING_FALLBACK_ROLE": "This role no longer exists.",
    "ORG_RELATIVE_STEP": "Who decides “{{step}}” depends on each document's unit and the org chart: test it with sample values to see who would decide.",
    "FALLBACK_NO_ONE": "No one in the fallback role of “{{step}}” can decide today (inactive, or without the “{{permission}}” permission): a document whose heads are all vacant or excluded would block.",
    "NO_ONE_CAN_DECIDE": "No one can decide “{{step}}” today (inactive, or without the “{{permission}}” permission): submissions would block.",
    "ONLY_ONE_CAN_DECIDE": "Only {{name}} can decide “{{step}}”: their own submissions will block.",
    "NO_HOLIDAYS": "Working-time reminders and due times skip holidays, but no holidays are entered for {{year}}: add them in Settings → Calendar.",
    "SHADOWED": "“{{name}}” (order {{order}}) applies to every document of type {{subject}} and comes first, so this workflow would never be selected."
  },
  "sentence": {
    "every": {
      "purchase": "Every purchase submitted for approval",
      "project_budget": "Every project budget submitted for approval",
      "project_variation": "Every project variation sent for internal approval",
      "project_claim": "Every progress claim sent for internal approval",
      "service_contract": "Every maintenance contract activated"
    },
    "when": "When",
    "onlyWhen": "Only when",
    "and": "and",
    "or": "or",
    "comma": ", ",
    "anything": "anything",
    "noneOf": "none of",
    "notAllOf": "not all of"
  },
  "names": {
    "hidden": {
      "supplier": "a supplier you cannot see",
      "supplierGroup": "a supplier group you cannot see",
      "warehouse": "a warehouse you cannot see",
      "role": "a role you cannot see",
      "user": "a user you cannot see",
      "orgUnit": "an org unit you cannot see",
      "projectType": "a project type you cannot see"
    }
  },
  "picker": {
    "supplier": "Choose a supplier",
    "addSupplier": "Add a supplier",
    "supplierGroup": "Choose supplier groups",
    "warehouse": "Choose a warehouse",
    "role": "Choose roles",
    "orgUnit": "Choose an org unit",
    "projectType": "Choose a project type",
    "search": "Search…",
    "empty": "Nothing matches.",
    "clear": "Clear",
    "remove": "Remove",
    "selected_one": "{{n}} selected",
    "selected_other": "{{n}} selected",
    "noAccess": {
      "supplier": "you cannot look up suppliers",
      "supplierGroup": "you cannot look up supplier groups",
      "warehouse": "you cannot look up warehouses",
      "role": "only editors of workflows can look up roles",
      "orgUnit": "org units are not available",
      "projectType": "you cannot look up project types, or Projects is turned off"
    },
    "enum": "Choose a value"
  },
  "conditions": {
    "add": "Add a condition",
    "addRule": "Add condition",
    "addGroup": "Add group",
    "removeAll": "Remove all conditions",
    "removeGroup": "Remove group",
    "removeRule": "Remove the condition on {{field}}",
    "match": "How the conditions combine",
    "matchAll": "All of these are true",
    "matchAny": "Any of these is true",
    "groupLegend": "Condition group, level {{depth}}",
    "emptyGroup": "An empty group constrains nothing.",
    "field": "Field",
    "operator": "Comparison",
    "valueOf": "Value for {{field}}",
    "if": "If",
    "mode": {
      "all": "All of these",
      "any": "Any of these",
      "none": "None of these",
      "notAll": "Not all of these"
    },
    "modeHint": {
      "none": "Holds when none of the conditions below is true.",
      "notAll": "Holds when at least one of the conditions below is false."
    },
    "textListHint": "Separate the values with commas."
  },
  "assignees": {
    "placeholder": "Choose users and roles",
    "search": "Search users and roles…",
    "roles": "Roles",
    "users": "Users",
    "chosen": "Chosen approvers",
    "none": "No one chosen yet",
    "roleTag": "(role)",
    "headOfSubject": "Head of the document’s unit",
    "headOfRequester": "Head of the requester’s unit",
    "hrbpOfSubject": "HR business partner of the document’s unit",
    "hrbpOfRequester": "HR business partner of the requester’s unit",
    "levelsUp": "(level +{{n}})",
    "roleMembers_one": "(role, {{n}} active member)",
    "roleMembers_other": "(role, {{n}} active members)",
    "fewPeople_one": "Only {{n}} person is named. If that person prepares or submits the purchase, no one can approve it and the request stops.",
    "fewPeople_other": "Only {{n}} people are named. If no one else can approve, the request stops.",
    "help": "Approvers are named users, existing roles, or heads and HR business partners found from the org chart for each document. An approver must also be active, hold permission to approve and read purchases, and never be the person who prepared or submitted the purchase.",
    "organisation": "Organisation",
    "orgHint": "found from the org chart for each document",
    "level": "Which head",
    "levelSelf": "The unit itself",
    "fallbackPrefix": "otherwise the role",
    "noFallback": "no fallback role chosen yet",
    "levelUp_one": "{{n}} level up",
    "levelUp_other": "{{n}} levels up"
  },
  "completion": {
    "any": "Any one of them",
    "anyHint": "The first eligible approver completes the step.",
    "all": "Everyone",
    "allHint": "Every eligible approver must approve.",
    "anyPrefix": "Any one of",
    "allPrefix": "Everyone in",
    "anyStamp": "Any one",
    "allStamp": "Everyone"
  },
  "timing": {
    "reminder": "reminder after {{time}}",
    "due": "due after {{time}}",
    "none": "no reminder or due time",
    "amount": {
      "HOURS_one": "{{n}} hour",
      "HOURS_other": "{{n}} hours",
      "WORKING_HOURS_one": "{{n}} working hour",
      "WORKING_HOURS_other": "{{n}} working hours",
      "WORKING_DAYS_one": "{{n}} working day",
      "WORKING_DAYS_other": "{{n}} working days"
    }
  },
  "step": {
    "defaultName": "Approval {{n}}",
    "nameEn": "Step name (English)",
    "nameAr": "Step name (Arabic)",
    "who": "Who approves",
    "whoPicker": "Approvers",
    "completion": "How many must approve",
    "when": "When this step runs",
    "whenShort": "Runs",
    "timingShort": "Timing",
    "separationShort": "Separation of duties",
    "always": "Always",
    "addWhen": "Run only when…",
    "reminder": "Reminder after",
    "due": "Due after",
    "separation": "Earlier approvers cannot approve this step",
    "separationHint": "Separation of duties: someone who approved an earlier step of the same request is left out here.",
    "separationOn": "Earlier approvers left out",
    "separationOff": "Earlier approvers may approve again",
    "blocked": "If no one can approve: the request stops and administrators are alerted.",
    "unit": "Count time in",
    "units": {
      "HOURS": "Calendar hours",
      "WORKING_HOURS": "Working hours",
      "WORKING_DAYS": "Working days"
    },
    "unitHint": {
      "HOURS": "Calendar hours from when the step starts, including nights, weekends and holidays.",
      "WORKING_HOURS": "Hours of the company’s working day, skipping weekends and public holidays.",
      "WORKING_DAYS": "Working days on the company calendar, skipping weekends and public holidays."
    },
    "orgTitle": "When the approver comes from the org chart",
    "fallback": "If no head can be found",
    "fallbackHint": "The members of this role decide when every head up the tree is vacant or left out.",
    "walkUp": "Levels to look up",
    "walkUpHint": "How many units above to try when a head is vacant or cannot approve (0 to 10)."
  },
  "builder": {
    "label": "Workflow steps, in order",
    "trigger": "Trigger",
    "triggerHint": "Fixed for this document type.",
    "appliesTo": "Applies to",
    "stepTitle": "Step {{n}}",
    "addTitle": "Add a step",
    "addStep": "Add approval step",
    "noSteps": "No approval steps yet. A workflow needs at least one before it can be published.",
    "outcomes": "Outcomes",
    "editStep": "Edit step",
    "editing": "Editing",
    "moveUp": "Move {{name}} up",
    "moveDown": "Move {{name}} down",
    "removeStep": "Remove {{name}}",
    "stepProblems_one": "{{n}} problem to fix",
    "stepProblems_other": "{{n}} problems to fix",
    "panel": "Step settings",
    "panelTitle": "Step {{n}} settings",
    "helpTitle": "How the builder works",
    "helpSelect": "Choose Edit step on a card to change its approvers, when it runs and its timing here.",
    "helpAssignees": "Only named users and existing roles can approve. There is no manager, department or branch routing.",
    "helpOrder": "Steps run from top to bottom; a later step starts when the one before it is complete.",
    "readOnlyTitle": "Read only",
    "readOnlyBody": "You can see this workflow but not change it.",
    "editsPublished": "You are viewing version {{n}}. Your changes become a new draft when you save.",
    "unsaved": "Unsaved changes",
    "fixFirst_one": "Fix {{n}} problem before saving.",
    "fixFirst_other": "Fix {{n}} problems before saving."
  },
  "outcome": {
    "approved": "Approved",
    "approvedLabel": "Action after approval",
    "leave": "Leave the purchase approved; someone who may post it will post it",
    "leaveHint": "The purchase waits, approved, for a person to post it.",
    "actionPermission": "Publishing this needs the “{{permission}}” permission, which you hold.",
    "actionPermissionMissing": "Publishing this needs the “{{permission}}” permission, which you do not hold.",
    "rejected": "Rejected",
    "rejectedText": "The request ends and the purchase returns to an editable draft. It can be resubmitted only after it is changed.",
    "returned": "Returned",
    "returnedText": "The request ends and the purchase goes back to the person who submitted it for changes; submitting it again starts a new request.",
    "actionAfter": "Action after approval"
  },
  "publish": {
    "title": "Publish {{name}}?",
    "body": "Draft {{n}} becomes the published version.",
    "problems_one": "{{n}} problem blocks publishing",
    "problems_other": "{{n}} problems block publishing",
    "warnings_one": "{{n}} warning",
    "warnings_other": "{{n}} warnings",
    "ready": "No blocking problems.",
    "showProblems": "Show in builder",
    "inStep": "Step {{n}} ({{name}})",
    "area": {
      "conditions": "Applies to",
      "steps": "Steps",
      "outcome": "Action after approval",
      "other": "Workflow"
    },
    "actionNote": "When approved, the purchase is posted automatically: {{action}}.",
    "actionMissing": "This version posts purchases automatically, and publishing it needs the “{{permission}}” permission, which you do not hold.",
    "impactTitle": "What happens",
    "impactRunning_one": "{{n}} request in progress stays on version {{version}} and finishes there.",
    "impactRunning_other": "{{n}} requests in progress stay on version {{version}} and finish there.",
    "impactFirst": "This is the first published version.",
    "impactNew": "New submissions from now on use version {{n}}.",
    "impactEnables": "Publishing also enables the workflow.",
    "nothing": "There are no changes to publish."
  },
  "detail": {
    "tabs": "Workflow sections",
    "disabledNote": "This workflow is disabled: new submissions skip it. Requests already in progress finish on their version.",
    "publishedTitle": "Published version {{n}}",
    "notPublished": "Not published yet",
    "notPublishedBody": "Build the steps, test them, then publish to start routing purchases.",
    "requestsTitle": "Requests now",
    "count": {
      "inProgress": "In progress",
      "blocked": "Blocked",
      "failed": "Action failed"
    },
    "runningNote": "Requests finish on the version they started with, whatever changes later."
  },
  "tabs": {
    "overview": "Overview",
    "builder": "Builder",
    "runs": "Requests",
    "versions": "Versions",
    "audit": "History"
  },
  "priority": {
    "title": "Order",
    "help": "When several workflows match a purchase, the one with the lowest number is used. Changing it changes routing, so only publishers can.",
    "change": "Change order",
    "invalid": "Enter a whole number from 1 to 9999.",
    "publisherOnly": "Only publishers can change the order."
  },
  "runs": {
    "label": "Requests of this workflow",
    "document": "Document",
    "status": "Status",
    "step": "Current step",
    "stepOf": "{{n}} of {{total}}",
    "requester": "Submitted by",
    "version": "Version",
    "submitted": "Submitted",
    "empty": "No requests yet",
    "emptyBody": "Requests appear here once purchases are submitted through this workflow.",
    "denied": "You cannot see approval requests",
    "deniedBody": "Monitoring requests needs its own permission.",
    "all": "All {{n}} requests"
  },
  "versions": {
    "number": "v{{n}}",
    "status": {
      "DRAFT": "Draft",
      "PUBLISHED": "Published",
      "RETIRED": "Retired"
    },
    "publishedBy": "Published {{at}} by {{name}}",
    "createdBy": "Created {{at}} by {{name}}",
    "runs_one": "{{n}} request",
    "runs_other": "{{n}} requests",
    "view": "View",
    "hide": "Hide",
    "empty": "No versions yet"
  },
  "audit": {
    "denied": "Viewing the history needs permission to read the audit log",
    "empty": "No changes recorded yet",
    "system": "System"
  },
  "exclusion": {
    "REQUESTER": "prepared or submitted the document",
    "USER_INACTIVE": "inactive user",
    "MISSING_PERMISSION": "does not have permission to approve purchases",
    "PRIOR_APPROVER": "approved an earlier step",
    "NOT_ASSIGNED": "not assigned to this step",
    "SUBJECT_MODULE_OFF": "Purchasing is turned off for your company"
  },
  "test": {
    "title": "Test the workflow",
    "subtitle": "See which workflow a purchase would get and who would approve it.",
    "target": "What to test",
    "targets": {
      "LIVE": "Live routing (every active workflow, in order)",
      "DRAFT": "This workflow's draft (version {{n}}) only",
      "PUBLISHED": "This workflow's published version {{n}} only"
    },
    "unsaved": "The test uses the saved draft. Save your changes to include them.",
    "source": "Test with",
    "synthetic": "Sample values",
    "syntheticHint": "Values you type; no real document is involved.",
    "syntheticLegend": "Sample values (synthetic)",
    "document": "A draft purchase",
    "documentHint": "The facts and the person who prepared it are taken from the purchase.",
    "documentDenied": "Needs permission to read purchases.",
    "purchase": "Purchase",
    "purchasePlaceholder": "Choose a draft purchase",
    "purchasesFailed": "Purchases could not be loaded.",
    "noPurchases": "No draft purchases match.",
    "requester": "Submitted by",
    "requesterHint": "Optional. That person is left out as an approver.",
    "noRequester": "No one in particular",
    "run": "Run test",
    "readOnly": "Read-only test — nothing was changed or sent",
    "readOnlySynthetic": "These results are for the sample values above.",
    "readOnlyDocument": "These results are for {{label}}; the purchase was not submitted.",
    "outcome": {
      "NO_APPROVAL_NEEDED": "No workflow applies: the purchase can be posted without approval.",
      "WOULD_WAIT_FOR_APPROVAL": "The purchase would wait for approval through {{name}} (version {{n}}).",
      "WOULD_BLOCK": "The request would stop: a step has no one who can approve it, and administrators would be alerted.",
      "ALREADY_PENDING": "This purchase is already waiting for approval."
    },
    "factsTitle": "Values used",
    "consideredTitle": "Workflows considered, in order",
    "noneConsidered": "No active workflow for this document type.",
    "consideredMeta": "v{{n}} · order {{order}}",
    "sources": {
      "DRAFT": "draft",
      "PUBLISHED": "published"
    },
    "matched": "Matches",
    "notMatched": "Does not match",
    "selected": "Selected",
    "earlierWon": "An earlier workflow in the order was selected first.",
    "noConditions": "No conditions: applies to every purchase.",
    "actual": "it is",
    "emptyValue": "empty",
    "passed": "Holds",
    "failed": "Does not hold",
    "stepsTitle": "Steps",
    "whenHeld": "Runs because:",
    "whenFailed": "Skipped because:",
    "skipped": "Skipped",
    "blocked": "Blocked",
    "eligible": "Would approve",
    "nobody": "No one can approve this step.",
    "excluded": "Left out",
    "missing_one": "{{n}} configured user or role no longer exists.",
    "missing_other": "{{n}} configured users or roles no longer exist.",
    "blockedText": "No eligible approver: the request would stop here and administrators would be alerted.",
    "finalTitle": "Action after approval",
    "finalAction": "Then: {{action}}",
    "group": {
      "all": "All of these",
      "any": "Any of these",
      "none": "None of these",
      "notAll": "Not all of these"
    },
    "holds": "holds",
    "notMet": "not met",
    "traceLabel": "Why it matched or not",
    "walks": "Passed over on the way up",
    "remindAt": "reminder on {{date}}",
    "dueAt": "due on {{date}}"
  },
  "changes": {
    "title": "What changes from version {{n}}",
    "first": "First version: there is no published version to compare with.",
    "none": "No changes from the published version.",
    "appliesTo": "Applies to",
    "before": "Before",
    "after": "After",
    "added": "Step added",
    "removed": "Step removed",
    "changed": "Step changed",
    "action": "Action after approval",
    "aspect": {
      "name": "name",
      "approvers": "approvers",
      "completion": "how many must approve",
      "when": "when it runs",
      "timing": "timing",
      "separation": "separation of duties",
      "order": "position"
    }
  },
  "subjectOff": {
    "purchase": {
      "title": "Purchasing is turned off for your company",
      "body": "Purchase workflows are kept, but they route nothing until Purchasing is turned back on: open purchase requests are on hold, and purchase workflows cannot be created or published meanwhile."
    },
    "project_budget": {
      "title": "Projects is turned off for your company",
      "body": "Project budget workflows are kept, but they route nothing until Projects is turned back on: open budget requests are on hold, and budget workflows cannot be created or published meanwhile."
    },
    "project_variation": {
      "title": "Projects is turned off for your company",
      "body": "Variation workflows are kept, but they route nothing until Projects is turned back on: open variation requests are on hold, and variation workflows cannot be created or published meanwhile."
    },
    "project_claim": {
      "title": "Projects is turned off for your company",
      "body": "Claim workflows are kept, but they route nothing until Projects is turned back on: open claim requests are on hold, and claim workflows cannot be created or published meanwhile."
    },
    "service_contract": {
      "title": "Maintenance contracts are turned off for your company",
      "body": "Contract workflows are kept, but they route nothing until maintenance contracts are turned back on: open contract requests are on hold, and contract workflows cannot be created or published meanwhile."
    }
  }
}
